import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const cached = localStorage.getItem("caos_user");
    return cached ? JSON.parse(cached) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("caos_access");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        localStorage.setItem("caos_user", JSON.stringify(data));
      })
      .catch(() => {
        localStorage.removeItem("caos_access");
        localStorage.removeItem("caos_refresh");
        localStorage.removeItem("caos_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("caos_access", data.access_token);
    localStorage.setItem("caos_refresh", data.refresh_token);
    localStorage.setItem("caos_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const setSessionFromTokens = useCallback((data) => {
    // Shared post-auth handler used by OTP verify + any future SSO flows.
    localStorage.setItem("caos_access", data.access_token);
    localStorage.setItem("caos_refresh", data.refresh_token);
    localStorage.setItem("caos_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Server-side session invalidation is best-effort; localStorage is always cleared below.
      console.debug("Logout request failed, continuing with local cleanup:", error?.message);
    }
    localStorage.removeItem("caos_access");
    localStorage.removeItem("caos_refresh");
    localStorage.removeItem("caos_user");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, setSessionFromTokens }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const ROLE_LABEL = {
  system: "System Admin",
  super_admin: "Super Admin",
  partner: "Partner",
  manager: "Manager",
  article_assistant: "Article Assistant",
  staff: "Staff",
};

export function hasRole(user, ...roles) {
  return user && roles.includes(user.role);
}

const LEVEL = { system: 6, super_admin: 5, partner: 4, manager: 3, article_assistant: 2, staff: 1 };
export function atLeast(user, minRole) {
  return user && (LEVEL[user.role] || 0) >= LEVEL[minRole];
}
